/**
 * AI Studio — universal conversation shell types.
 *
 * These types are deliberately generic: the shell is one conversation foundation
 * with explicit contexts (REQ-AIS-038). A conversation is always bound to a
 * context_type; AGENT_RUN conversations additionally carry a run / binding.
 *
 * The service maps the backend's snake_case rows into these camelCase view models.
 * Keeping that boundary explicit prevents database field names from leaking into
 * shell components.
 */

/** Conversation context contract. */
export type AiStudioContextType =
  | 'TMOS_OPERATIONS'
  | 'AGENT_RUN'
  | 'AGENT_AUTHORING'
  | 'INSTANCE_SETUP'
  | 'PACK_AUTHORING'
  | 'INSTANCE_DEPLOY'
  | 'INSTANCE_PROVISION';

export type AiStudioConversationStatus = 'open' | 'closed' | 'archived';

export type ConversationMessageRole = 'user' | 'assistant' | 'system';

/** Clickable choice in the conversation (e.g. Plan A / Plan B). */
export interface ConversationChoice {
  id: string;
  label: string;
  sendText: string;
}

/**
 * A single message in the shell.
 * `pending` is client-only: the assistant bubble is created optimistically while
 * the current non-streaming transport waits for the authoritative reply.
 */
export interface ConversationMessage {
  id: string;
  role: ConversationMessageRole;
  content: string;
  createdAt: string;
  tokensUsed?: number | null;
  pending?: boolean;
  /** Assistant-offered choices rendered as chips; click sends `sendText`. */
  choices?: ConversationChoice[];
  /** Who typed this user bubble (team project thread). */
  authorName?: string | null;
}

/**
 * Free-form pointer to whatever the context is bound to (page/entity for
 * TMOS_OPERATIONS, scope descriptors for AGENT_RUN). Kept opaque on the client;
 * only well-known display fields are read by the context indicator.
 */
export interface ConversationContextRef {
  /** Human-readable scope shown in the active context indicator (e.g. "Plant 4 — Q3 schedule"). */
  label?: string;
  [key: string]: unknown;
}

export interface AiStudioConversation {
  id: string;
  tenantId: string;
  runId: string | null;
  bindingId: string | null;
  contextType: AiStudioContextType;
  status: AiStudioConversationStatus;
  contextRef: ConversationContextRef;
  shellMetadata: Record<string, unknown>;
  /** Optional display name derived by the backend (e.g. first user message). */
  name?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Row shape for the conversation picker (list view). */
export interface AiStudioConversationSummary {
  id: string;
  name?: string | null;
  contextType: AiStudioContextType;
  status: AiStudioConversationStatus;
  runId: string | null;
  bindingId: string | null;
  shellMetadata?: Record<string, unknown>;
  /** Reserved for a future list projection; the current backend does not return it. */
  messageCount?: number;
  createdAt: string;
  updatedAt: string;
}

/** Authoritative messages returned by the backend after a completed turn. */
export interface ConversationSendResult {
  userMessage: ConversationMessage;
  assistantMessage: ConversationMessage;
  tokensUsed: number;
}

/** What the ActiveContextIndicator renders. Derived from a conversation. */
export interface ActiveConversationContext {
  contextType: AiStudioContextType;
  /** Scope line, e.g. run name or bound page/entity. */
  scopeLabel?: string | null;
  status?: AiStudioConversationStatus;
}

/**
 * Derive indicator input from a conversation. Keeps the indicator presentational
 * so it can also be driven by a not-yet-persisted (draft) context.
 */
export function toActiveContext(conversation: AiStudioConversation): ActiveConversationContext {
  const scopeLabel =
    typeof conversation.contextRef?.label === 'string' && conversation.contextRef.label.trim() !== ''
      ? conversation.contextRef.label
      : conversation.name ?? null;
  return {
    contextType: conversation.contextType,
    scopeLabel,
    status: conversation.status,
  };
}
